import { mapValues } from "lodash"
import type { SingleDateGathererDataItem, SingleDateGathererMetricValue } from "gatherers/types"
import {
  BATTING_TOTAL,
  BATTING_ALPHA,
  BATTING_FACTORS,
  SLUGGING_TOTAL,
  SLUGGING_ALPHA,
  SLUGGING_FACTORS,
} from "gatherers/single-date/period-performance"

/**
 * Type-safe mapping of batting/slugging metric constants to their display labels
 */
const LABEL_REPLACEMENTS = {
  [BATTING_TOTAL]: "Batting",
  [BATTING_ALPHA]: "Batting",
  [BATTING_FACTORS]: "Batting",
  [SLUGGING_TOTAL]: "Slugging",
  [SLUGGING_ALPHA]: "Slugging",
  [SLUGGING_FACTORS]: "Slugging",
} as const satisfies Record<string, string>

/**
 * Type representing the metric IDs that have label replacements
 */
type ReplacementMetricId = keyof typeof LABEL_REPLACEMENTS

/**
 * Transforms decision quality metric labels for the Compare context.
 * Replaces generic labels like "Total", "Alpha", "Factors" with specific labels
 * like "Batting" and "Slugging" to distinguish between the two metric types
 * when they appear mixed together in the same section.
 *
 * In the KPI Decisions context, metrics are in separate sections with clear headers,
 * so the original generic labels are preserved.
 *
 * @param data - Array of metric data objects, each containing a values object
 * @returns Transformed data with replaced labels for batting/slugging metrics
 */
export function transformDecisionQualityLabels(
  data: readonly SingleDateGathererDataItem[],
): SingleDateGathererDataItem[] {
  return data.map((item) => ({
    ...item,
    values: mapValues(item.values, (metric, id): SingleDateGathererMetricValue => {
      const replacementLabel = LABEL_REPLACEMENTS[id as ReplacementMetricId]
      if (!replacementLabel) {
        return metric
      }

      return {
        ...metric,
        label: replacementLabel,
      }
    }),
  }))
}
